import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { translations, type Language, type TranslationShape } from './translations'

const STORAGE_KEY = 'medispa-language'

interface LanguageContextValue {
  language: Language
  setLanguage: (language: Language) => void
  toggleLanguage: () => void
  t: TranslationShape
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

function readInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'hr'
  try {
    const stored = window.localStorage?.getItem(STORAGE_KEY)
    if (stored === 'hr' || stored === 'en') return stored
  } catch {
    // localStorage may be unavailable (private mode / sandboxed iframe)
  }
  return 'hr'
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => readInitialLanguage())

  useEffect(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, language)
    } catch {
      // ignore write failures
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language
    }
  }, [language])

  const setLanguage = useCallback((next: Language) => {
    setLanguageState(next)
  }, [])

  const toggleLanguage = useCallback(() => {
    setLanguageState((prev) => (prev === 'hr' ? 'en' : 'hr'))
  }, [])

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage,
      toggleLanguage,
      t: translations[language]
    }),
    [language, setLanguage, toggleLanguage]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext)
  if (!context) {
    // Safe fallback so a component rendered outside the provider never crashes
    return {
      language: 'hr',
      setLanguage: () => undefined,
      toggleLanguage: () => undefined,
      t: translations.hr
    }
  }
  return context
}
